import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import type { ChannelFileContentSource } from '../../lib/api';
import {
  createPdfLoadingTask,
  isCancelledPdfRender,
  isInvalidPdfError,
  type PdfLoadingTask,
} from './pdfRuntime';

type LoadFailure = 'password' | 'invalid' | 'network';

export function PdfViewer({ source }: { source: ChannelFileContentSource }) {
  const { t } = useLingui();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [loadFailure, setLoadFailure] = useState<LoadFailure | null>(null);
  const [renderFailed, setRenderFailed] = useState(false);
  const [rendering, setRendering] = useState(false);
  const devUserId = source.httpHeaders?.['X-Dev-User-Id'];

  useEffect(() => {
    let active = true;
    let passwordRejected = false;
    let loadingTask: PdfLoadingTask | null = null;

    setDocument(null);
    setPageNumber(1);
    setScale(1);
    setLoadFailure(null);
    setRenderFailed(false);

    void (async () => {
      try {
        loadingTask = await createPdfLoadingTask(source);
        if (!active) {
          await loadingTask.destroy();
          return;
        }
        loadingTask.onPassword = () => {
          passwordRejected = true;
          if (active) setLoadFailure('password');
          void loadingTask?.destroy();
        };
        const loadedDocument = await loadingTask.promise;
        if (!active || passwordRejected) {
          await loadingTask.destroy();
          return;
        }
        setDocument(loadedDocument);
      } catch (error) {
        if (!active || passwordRejected) return;
        setLoadFailure(isInvalidPdfError(error) ? 'invalid' : 'network');
      }
    })();

    return () => {
      active = false;
      void loadingTask?.destroy();
    };
  }, [source.url, devUserId]);

  useEffect(() => {
    if (!document || !canvasRef.current) return;

    let active = true;
    let renderTask: RenderTask | null = null;
    let loadedPage: Awaited<ReturnType<PDFDocumentProxy['getPage']>> | null = null;
    setRendering(true);
    setRenderFailed(false);

    void (async () => {
      try {
        const page = await document.getPage(pageNumber);
        loadedPage = page;
        if (!active || !canvasRef.current) {
          page.cleanup();
          loadedPage = null;
          return;
        }
        const viewport = page.getViewport({ scale });
        const outputScale = Math.max(window.devicePixelRatio || 1, 1);
        const canvas = canvasRef.current;
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        renderTask = page.render({
          canvas,
          viewport,
          transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0],
        });
        await renderTask.promise;
        if (active) setRendering(false);
      } catch (error) {
        if (!active || isCancelledPdfRender(error)) return;
        setRendering(false);
        setRenderFailed(true);
      } finally {
        loadedPage?.cleanup();
        loadedPage = null;
      }
    })();

    return () => {
      active = false;
      renderTask?.cancel();
      loadedPage?.cleanup();
      loadedPage = null;
    };
  }, [document, pageNumber, scale]);

  if (loadFailure === 'password') {
    return <div className="file-viewer-error" role="alert"><Trans>This PDF is password protected and cannot be previewed</Trans></div>;
  }
  if (loadFailure === 'invalid') {
    return <div className="file-viewer-error" role="alert"><Trans>This PDF is damaged or is not a valid PDF</Trans></div>;
  }
  if (loadFailure === 'network') {
    return <div className="file-viewer-error" role="alert"><Trans>Failed to load PDF preview</Trans></div>;
  }
  if (!document) {
    return <div className="file-viewer-loading"><Trans>Loading PDF…</Trans></div>;
  }

  return (
    <div className="pdf-viewer">
      <div className="pdf-viewer-toolbar" role="toolbar" aria-label={t`PDF preview controls`}>
        <button
          type="button"
          disabled={pageNumber <= 1}
          onClick={() => setPageNumber(current => Math.max(1, current - 1))}
        >
          <Trans>Previous page</Trans>
        </button>
        <span><Trans>Page {pageNumber} of {document.numPages}</Trans></span>
        <button
          type="button"
          disabled={pageNumber >= document.numPages}
          onClick={() => setPageNumber(current => Math.min(document.numPages, current + 1))}
        >
          <Trans>Next page</Trans>
        </button>
        <button
          type="button"
          aria-label={t`Zoom out`}
          disabled={scale <= 0.5}
          onClick={() => setScale(current => Math.max(0.5, current - 0.25))}
        >
          −
        </button>
        <span>{Math.round(scale * 100)}%</span>
        <button
          type="button"
          aria-label={t`Zoom in`}
          disabled={scale >= 3}
          onClick={() => setScale(current => Math.min(3, current + 0.25))}
        >
          +
        </button>
      </div>
      <div className="pdf-viewer-page">
        {rendering && <div className="pdf-viewer-rendering"><Trans>Rendering page…</Trans></div>}
        {renderFailed && <div className="file-viewer-error" role="alert"><Trans>Failed to render this PDF page</Trans></div>}
        <canvas
          ref={canvasRef}
          aria-label={t`PDF page ${pageNumber}`}
          hidden={renderFailed}
        />
      </div>
    </div>
  );
}
